import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AddPageCountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookId: string;
  bookTitle: string;
  onPageCountAdded: () => void;
}

export function AddPageCountDialog({
  open,
  onOpenChange,
  bookId,
  bookTitle,
  onPageCountAdded
}: AddPageCountDialogProps) {
  const [pageCount, setPageCount] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const count = parseInt(pageCount);
    
    if (isNaN(count) || count < 1) {
      toast({
        variant: "destructive",
        title: "Invalid page count",
        description: "Please enter a valid number greater than 0"
      });
      return;
    }

    if (count > 5000) {
      toast({
        variant: "destructive",
        title: "Page count too high",
        description: "Page count must be less than 5000. If this is a collection, please split it into individual books."
      });
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('You must be logged in to contribute page counts');
      }

      const { error } = await supabase
        .from('books')
        .update({
          page_count: count,
          page_count_source: 'user_contributed',
          page_count_estimated: false,
          page_count_contributed_by: user.id
        })
        .eq('id', bookId);

      if (error) throw error;

      toast({
        title: "Page count added",
        description: "Thank you for contributing! This helps improve reading stats for everyone."
      });

      onPageCountAdded();
      onOpenChange(false);
      setPageCount('');
    } catch (error) {
      console.error('Error adding page count:', error);
      toast({
        variant: "destructive",
        title: "Failed to add page count",
        description: error instanceof Error ? error.message : "Please try again"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Page Count</DialogTitle>
          <DialogDescription>
            Help improve reading stats by adding the page count for "{bookTitle}"
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pageCount">Number of Pages</Label>
            <Input
              id="pageCount"
              type="number"
              min="0"
              max="5000"
              value={pageCount}
              onChange={(e) => setPageCount(e.target.value)}
              placeholder="e.g. 320"
              disabled={loading}
            />
          </div>

          {pageCount && parseInt(pageCount) > 1000 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Large page count detected. Please verify this is correct and not a collection of multiple books.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Page Count'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
